import { Component, computed, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  LogOut,
  Beaker,
  Settings,
  User,
  FileText,
  Shield,
  FileCheck,
} from 'lucide-angular';
import { AuthService } from '../../core/auth/auth.service';
import { UserRole } from '../../core/models/user.model';
import { getHttpErrorMessage } from '../../core/http/http-error';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule],
  templateUrl: './layout.component.html',
})
export class LayoutComponent implements OnInit {
  currentPath = '';

  // Icons
  icons = {
    LogOut,
    Beaker,
    Settings,
    User,
    FileText,
    Shield,
    FileCheck,
  };

  // Computed from AuthService
  userName = computed(() => this.auth.currentUser()?.name ?? 'Utilisateur');
  isSuperAdmin = computed(() => this.auth.isSuperAdmin());
  isAdmin = computed(() => {
    const user = this.auth.currentUser();
    return [UserRole.ADMIN, UserRole.ADMIN_SUPPORT].includes(user?.role as UserRole);
  });
  labName = computed(() => this.auth.currentUser()?.labName ?? null);
  public isExecutor = computed(() => this.auth.isExecutor());
  email = computed(() => this.auth.currentUser()?.email);
  isAuthenticated = computed(() => this.auth.isAuthenticated());
  isCommercial = computed(() => this.auth.isCommercial());
  canSeeLaboratories = this.isSuperAdmin() || (this.isAdmin() && this.isExecutor());
  canSeeUsers = this.isSuperAdmin() || this.isAdmin();
  canSeeAnalysisTypes = this.isSuperAdmin() || this.isAdmin();
  canSeeRegistrations =
    this.isSuperAdmin() || (this.isAdmin() && this.isExecutor()) || this.isCommercial();
  showUserMenu = false;
  @ViewChild('userMenuContainer') userMenuContainerRef!: ElementRef;
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showUserMenu) {
      this.showUserMenu = false;
    }
  }

  constructor(
    private readonly router: Router,
    private readonly auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentPath = event.urlAfterRedirects;
      });

    // Initial path
    this.currentPath = this.router.url;

    // Si on a un token mais pas de user en mémoire, on fetch le profil
    if (this.auth.isAuthenticated() && !this.auth.currentUser()) {
      this.auth.fetchMe().subscribe();
    }
  }

  handleLogout(): void {
    this.closeUserMenu();
    this.auth.logout().subscribe({
      next: () => {
        this.router.navigate(['/auth']);
      },
      error: (err) => {
        console.error(getHttpErrorMessage(err, 'Erreur logout'));
        this.router.navigate(['/auth']);
      },
    });
  }

  get isAdminZone(): boolean {
    return this.currentPath.startsWith('/admin');
  }

  /** Page d'accueil utilisateur (lab à gauche, nom + Logout à droite) */
  get isHomePage(): boolean {
    return this.currentPath === '/home';
  }

  isActive(path: string): boolean {
    if (path === '/home/dashboard' && this.currentPath === '/home/dashboard') return true;
    if (path === '/admin' && this.currentPath === '/admin') return true;
    if (path === '/admin/laboratories' && this.currentPath.startsWith('/admin/laboratories'))
      return true;
    if (path === '/admin/users' && this.currentPath.startsWith('/admin/users')) return true;
    if (path === '/admin/analysis-types' && this.currentPath.startsWith('/admin/analysis-types'))
      return true;
    if (path === '/home/demands' && this.currentPath.startsWith('/home/demands')) return true;
    return false;
  }

  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  toggleUserMenu(event: MouseEvent) {
    event.stopPropagation();
    this.showUserMenu = !this.showUserMenu;
  }

  closeUserMenu() {
    this.showUserMenu = false;
  }

  openProfile() {
    this.closeUserMenu();
    this.router.navigate(['/profile']);
  }

  openInfoPage(page: 'general-conditions' | 'privacy-policy' | 'legal-mentions') {
    this.closeUserMenu();
    this.router.navigate([`info/${page}`]);
  }
}
